import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '@/lib/utils'
import { MARKET } from '@/market'

/** One tab per sales point, in the order the value proposition names them. */
const TAB_KEYS = ['pos', 'storefront', 'qr', 'waiter', 'kitchen', 'warehouse'] as const
type TabKey = (typeof TAB_KEYS)[number]

/** Shorthand for the mockup label namespace. */
function useMock() {
  const { t } = useTranslation()
  return (key: string) => t(`features.mock.${key}`)
}

// ── Mockups ────────────────────────────────────────────────────────

function PosMockup() {
  const m = useMock()
  const statuses = [
    { key: 'st_free', cls: 'border-dark-border text-white/30' },
    { key: 'st_paid', cls: 'border-primary/50 text-primary' },
    { key: 'st_waiting', cls: 'border-accent/50 text-accent' },
    { key: 'st_ordered', cls: 'border-blue-500/50 text-blue-400' },
  ]
  return (
    <div className="bg-dark-bg rounded-2xl border border-dark-border p-4">
      <p className="text-[10px] text-white/30 tracking-widest uppercase mb-3">{m('hall')}</p>
      <div className="grid grid-cols-4 gap-2 mb-3">
        {Array.from({ length: 12 }, (_, i) => {
          const s = statuses[i % statuses.length]
          return (
            <div key={i} className={`rounded-xl border bg-dark-card p-2.5 text-center ${s.cls}`}>
              <div className="font-bold text-[13px]">№{i + 1}</div>
              <div className="text-[10px] opacity-80 mt-0.5">{m(s.key)}</div>
            </div>
          )
        })}
      </div>
      <div className="flex gap-2">
        <button className="flex-1 bg-primary rounded-xl py-2.5 text-[13px] font-bold text-white">
          {m('accept_payment')}
        </button>
        <button className="bg-dark-card border border-dark-border rounded-xl px-4 text-[13px] text-white/60">
          {m('pin')}
        </button>
      </div>
    </div>
  )
}

function StorefrontMockup() {
  const m = useMock()
  const dishes = [
    { key: 'plov', price: MARKET.mockMoney(35000), out: false },
    { key: 'lagman', price: MARKET.mockMoney(32000), out: false },
    { key: 'shashlik', price: MARKET.mockMoney(45000), out: true },
  ]
  return (
    <div className="flex justify-center">
      <div className="w-[230px] bg-dark-bg rounded-3xl border border-dark-border p-4 shadow-2xl">
        <div className="flex items-center gap-1.5 mb-3">
          <span className="w-1.5 h-1.5 rounded-full bg-primary" />
          <p className="text-[10px] text-white/35 tracking-wide">{m('storefront_head')}</p>
        </div>

        <div className="space-y-2">
          {dishes.map(d => (
            <div
              key={d.key}
              className={cn(
                'rounded-xl border p-2.5',
                d.out ? 'border-dark-border bg-dark-card/40' : 'border-dark-border bg-dark-card'
              )}
            >
              <div className="flex items-baseline justify-between gap-2">
                <span className={cn('text-[12.5px] font-semibold', d.out ? 'text-white/25 line-through' : 'text-white')}>
                  {m(`dishes.${d.key}`)}
                </span>
                <span className={cn('text-[12px]', d.out ? 'text-white/20' : 'text-white/55')}>{d.price}</span>
              </div>
              <button
                disabled={d.out}
                className={cn(
                  'mt-2 w-full rounded-lg py-1.5 text-[11px] font-semibold',
                  d.out ? 'bg-dark-bg text-white/25' : 'bg-primary/15 text-primary'
                )}
              >
                {d.out ? m('sold_out') : m('add_to_cart')}
              </button>
            </div>
          ))}
        </div>

        <button className="mt-3 w-full bg-primary rounded-xl py-2 text-[12px] font-bold text-white">
          {m('checkout')}
        </button>
      </div>
    </div>
  )
}

function QrMockup() {
  const m = useMock()
  const items = [
    { key: 'plov', qty: 1, price: MARKET.mockMoney(35000) },
    { key: 'lagman', qty: 1, price: MARKET.mockMoney(32000) },
    { key: 'tea', qty: 2, price: MARKET.mockMoney(12000) },
  ]
  return (
    <div className="flex justify-center">
      <div className="w-[210px] bg-dark-bg rounded-3xl border border-dark-border p-4 shadow-2xl">
        <div className="text-[10px] text-white/35 mb-0.5">{m('table')} 4 · {MARKET.mockVenueName}</div>
        <div className="text-[15px] font-bold text-white mb-3">{m('your_order')}</div>
        {items.map(i => (
          <div key={i.key} className="flex justify-between text-[12px] mb-1.5">
            <span className="text-white/60">
              {m(`dishes.${i.key}`)} ×{i.qty}
            </span>
            <span className="text-white/60">{i.price}</span>
          </div>
        ))}
        <div className="border-t border-dark-border my-2.5" />
        <div className="flex justify-between text-[13px] font-bold text-white mb-3">
          <span>{m('total')}</span>
          <span>
            {MARKET.mockMoney(79000)} {m('currency')}
          </span>
        </div>
        <button className="w-full bg-primary rounded-xl py-2 text-[12px] font-bold text-white mb-2">
          {m('pay_click')}
        </button>
        <button className="w-full border border-dark-border rounded-xl py-2 text-[12px] text-white/50">
          {m('pay_payme')}
        </button>
      </div>
    </div>
  )
}

function WaiterMockup() {
  const m = useMock()
  const orders = [
    { table: 4, guests: 4, items: 6, status: 'cooking', color: 'text-accent' },
    { table: 7, guests: 2, items: 3, status: 'ready', color: 'text-primary' },
    { table: 11, guests: 6, items: 9, status: 'accepted', color: 'text-blue-400' },
  ]
  return (
    <div className="bg-dark-card rounded-2xl border border-dark-border p-4">
      <div className="flex justify-between items-center mb-3">
        <p className="text-[10px] text-white/30 tracking-widest uppercase">{m('my_tables')}</p>
        <span className="text-[11px] text-primary font-medium">● {m('online')}</span>
      </div>
      <div className="space-y-2">
        {orders.map(o => (
          <div
            key={o.table}
            className="bg-dark-bg rounded-xl border border-dark-border p-3 flex justify-between items-center gap-3"
          >
            <div className="min-w-0">
              <div className="text-[13px] font-semibold text-white">
                {m('table')} {o.table} · {o.guests} {m('guests')}
              </div>
              <div className="text-[11px] text-white/35 mt-0.5">
                {o.items} {m('positions')}
              </div>
            </div>
            <span className={`text-[13px] font-semibold shrink-0 ${o.color}`}>{m(o.status)}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

function KitchenMockup() {
  const m = useMock()
  const cards = [
    { id: `${m('table')} 4`, time: '2:14', items: ['plov', 'lagman'], urgent: false },
    { id: `${m('delivery')} #218`, time: '0:42', items: ['shashlik', 'salad'], urgent: false },
    { id: `${m('table')} 9`, time: '5:30', items: ['manti'], urgent: true },
    { id: `${m('table')} 2`, time: '0:18', items: ['soup'], urgent: false },
  ]
  return (
    <div className="bg-dark-card rounded-2xl border border-dark-border p-4">
      <p className="text-[10px] text-white/30 tracking-widest uppercase mb-3">{m('kitchen_station')}</p>
      <div className="grid grid-cols-2 gap-2">
        {cards.map(c => (
          <div
            key={c.id}
            className={cn(
              'rounded-xl border p-3',
              c.urgent ? 'border-red-500/50 bg-red-500/10' : 'border-dark-border bg-dark-bg'
            )}
          >
            <div className="flex justify-between gap-2 mb-1.5">
              <span className="text-[13px] font-bold text-white truncate">{c.id}</span>
              <span
                className={cn('text-[13px] font-mono font-bold shrink-0', c.urgent ? 'text-red-400' : 'text-primary')}
              >
                {c.time}
              </span>
            </div>
            {c.items.map(i => (
              <div key={i} className="text-[11px] text-white/50">
                • {m(`dishes.${i}`)}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

function WarehouseMockup() {
  const m = useMock()
  const ingredients = [
    { key: 'rice', used: `180 ${m('units.g')}`, left: `4.2 ${m('units.kg')}`, low: false },
    { key: 'oil', used: `25 ${m('units.ml')}`, left: `1.8 ${m('units.l')}`, low: false },
    { key: 'meat', used: `120 ${m('units.g')}`, left: `0.4 ${m('units.kg')}`, low: true },
    { key: 'carrot', used: `90 ${m('units.g')}`, left: `2.6 ${m('units.kg')}`, low: false },
  ]
  return (
    <div className="bg-dark-card rounded-2xl border border-dark-border p-4">
      <p className="text-[10px] text-white/30 tracking-widest uppercase mb-1">{m('warehouse_head')}</p>
      <p className="text-[15px] font-bold text-white mb-3">{m('warehouse_sold')}</p>

      <div className="space-y-2">
        {ingredients.map(ing => (
          <div
            key={ing.key}
            className="flex items-center justify-between gap-3 rounded-xl border border-dark-border bg-dark-bg p-2.5"
          >
            <div className="flex items-center gap-2 min-w-0">
              <span className={cn('w-1.5 h-1.5 rounded-full shrink-0', ing.low ? 'bg-red-400' : 'bg-primary')} />
              <span className="text-[12.5px] text-white/75 truncate">{m(`ingredients.${ing.key}`)}</span>
            </div>
            <div className="flex items-baseline gap-2 shrink-0">
              <span className="text-[12px] font-mono text-red-400/80">−{ing.used}</span>
              <span className={cn('text-[11px]', ing.low ? 'text-red-400' : 'text-white/35')}>
                {ing.left} {m('warehouse_left')}
              </span>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-3 flex items-center gap-2 rounded-xl border border-red-500/30 bg-red-500/10 px-3 py-2">
        <span className="text-[11px] font-bold uppercase tracking-wide text-red-400">{m('stop_list')}</span>
        <span className="text-[12px] text-white/60 truncate">{m('dishes.shashlik')}</span>
      </div>
    </div>
  )
}

const MOCKUPS: Record<TabKey, React.ReactNode> = {
  pos: <PosMockup />,
  storefront: <StorefrontMockup />,
  qr: <QrMockup />,
  waiter: <WaiterMockup />,
  kitchen: <KitchenMockup />,
  warehouse: <WarehouseMockup />,
}

// ── Main section ───────────────────────────────────────────────────

export function Features() {
  const { t } = useTranslation()
  const [active, setActive] = useState<TabKey>('pos')

  const tab = t(`features.${active}`, { returnObjects: true }) as { title: string; points: string[] }

  return (
    <section id="features" className="bg-dark-bg py-[96px]">
      <div className="mx-auto max-w-[1200px] px-4 sm:px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-10"
        >
          <p className="text-[11px] font-semibold tracking-[0.12em] uppercase text-primary mb-3">
            {t('nav.features')}
          </p>
          <h2 className="text-white text-[32px] sm:text-[44px] font-bold leading-[1.1] tracking-[-0.02em]">
            {t('features.title')}
          </h2>
          <p className="mt-3 text-white/50 text-[16px] sm:text-[18px]">{t('features.subtitle')}</p>
        </motion.div>

        {/* Segmented tabs */}
        <div className="flex justify-center mb-12 px-2">
          <div className="bg-dark-card border border-dark-border rounded-full p-1 flex flex-wrap justify-center gap-1">
            {TAB_KEYS.map(key => (
              <button
                key={key}
                onClick={() => setActive(key)}
                aria-pressed={active === key}
                className={cn(
                  'rounded-full px-4 py-2 text-[13px] font-medium transition-all whitespace-nowrap',
                  active === key ? 'bg-white text-foreground shadow-sm' : 'text-white/45 hover:text-white/80'
                )}
              >
                {t(`features.tabs.${key}`)}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.22, ease: 'easeOut' }}
            className="grid lg:grid-cols-[45%_55%] gap-10 lg:gap-16 items-center"
          >
            <div>
              <h3 className="text-white text-[26px] sm:text-[32px] font-bold leading-[1.2] tracking-[-0.02em] mb-6">
                {tab.title}
              </h3>
              <ul className="space-y-3.5">
                {tab.points.map((point, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" strokeWidth={2.5} />
                    <span className="text-white/75 text-[15px] leading-[1.55]">{point}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>{MOCKUPS[active]}</div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  )
}
